/**
 * Lobby (UX-SPEC §4): the tile wordmark, "Create a game" and "Join a game"
 * cards, the Resume list of rooms that still have a seat token, a How to play
 * disclosure and the footer (theme, sound, version pill).
 */
import { useEffect, useId, useLayoutEffect, useState, type FormEvent } from 'react';
import { flushSync } from 'react-dom';
import clsx from 'clsx';
import {
  DEFAULT_SETTINGS,
  NAME_MAX_LENGTH,
  PASS_ROUND_OPTIONS,
  ROOM_CODE_LENGTH,
  TURN_TIME_OPTIONS,
} from '@wordloom/engine';
import { focusFirstInvalid } from '../lib/focus';
import { forgetRoom, getRecentRooms, type RecentRoom } from '../lib/storage';
import { relativeTime, turnTimeLabel } from '../lib/time';
import { createAndEnter, joinWithName, storedName } from '../net/session';
import { navigate, parseRoomInput, roomPath, sanitizeCodeTyping } from '../router';
import { useRoomStore } from '../state/roomStore';
import { HowToPlayContent } from './HowToPlay';
import { IconArrowRight, IconClose, IconUser } from './Icons';
import { SoundToggle } from './SoundToggle';
import { ThemeToggle } from './ThemeToggle';
import { VersionPill } from './VersionPill';
import { Wordmark } from './Wordmark';
import './Lobby.css';

/** Shared validation for the name fields ("" when fine). */
export function nameError(name: string): string {
  if (!name.trim()) return 'Enter a name so your opponent knows who they’re playing.';
  return '';
}

function passLabel(n: number): string {
  return `${n} ${n === 1 ? 'pass' : 'passes'} each`;
}

function NameField({
  value,
  onChange,
  error,
  autoFocus,
}: {
  value: string;
  onChange: (v: string) => void;
  error: string;
  autoFocus?: boolean;
}) {
  const id = useId();
  // The error sits outside the <label>: inside it, it became part of the
  // field's name and was read twice (name + description).
  return (
    <div className="field">
      <label className="field__label" htmlFor={id}>
        Your name
      </label>
      <input
        id={id}
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        maxLength={NAME_MAX_LENGTH}
        autoComplete="nickname"
        placeholder="e.g. Maya"
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? `${id}-err` : undefined}
        autoFocus={autoFocus}
        enterKeyHint="go"
      />
      {error && (
        <span id={`${id}-err`} className="field__error">
          {error}
        </span>
      )}
    </div>
  );
}

function CreateCard({ name, setName }: { name: string; setName: (v: string) => void }) {
  const [timer, setTimer] = useState<number | null>(DEFAULT_SETTINGS.turnTimeLimitSec);
  const [passes, setPasses] = useState<number>(DEFAULT_SETTINGS.passRoundsToEnd);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [nameErr, setNameErr] = useState('');
  const timerId = useId();
  const passId = useId();

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    const form = e.currentTarget;
    const ne = nameError(name);
    // Commit aria-invalid + the error text before moving focus, so the field
    // is announced together with its error.
    flushSync(() => setNameErr(ne));
    if (ne) {
      focusFirstInvalid(form);
      return;
    }
    setBusy(true);
    setError('');
    const res = await createAndEnter(name, { turnTimeLimitSec: timer, passRoundsToEnd: passes });
    setBusy(false);
    if (!res.ok) setError(res.error.message);
  };

  return (
    <form className="card lobby-card" onSubmit={(e) => void submit(e)} aria-labelledby="create-title" noValidate>
      <h2 id="create-title" className="lobby-card__title">
        Create a game
      </h2>
      <p className="lobby-card__lede">Start a room and send the link to a friend.</p>
      <NameField
        value={name}
        onChange={(v) => {
          setName(v);
          if (nameErr) setNameErr('');
        }}
        error={nameErr}
      />
      <div className="lobby-card__pair">
        <label className="field" htmlFor={timerId}>
          <span className="field__label">Turn timer</span>
          <select
            id={timerId}
            className="select"
            value={timer === null ? 'none' : String(timer)}
            onChange={(e) => setTimer(e.target.value === 'none' ? null : Number(e.target.value))}
          >
            {TURN_TIME_OPTIONS.map((t) => (
              <option key={t ?? 'none'} value={t === null ? 'none' : String(t)}>
                {t === null ? 'Untimed' : turnTimeLabel(t).replace(' per turn', '')}
              </option>
            ))}
          </select>
        </label>
        <label className="field" htmlFor={passId}>
          <span className="field__label">Game ends after</span>
          <select id={passId} className="select" value={passes} onChange={(e) => setPasses(Number(e.target.value))}>
            {PASS_ROUND_OPTIONS.map((n) => (
              <option key={n} value={n}>
                {passLabel(n)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn--primary btn--block lobby-card__cta" aria-disabled={busy || undefined}>
        {busy && <span className="btn__spinner" aria-hidden="true" />}
        Create room
      </button>
    </form>
  );
}

function JoinCard({ name, setName }: { name: string; setName: (v: string) => void }) {
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [codeErr, setCodeErr] = useState('');
  const [nameErr, setNameErr] = useState('');
  const codeId = useId();

  const onCodeChange = (raw: string) => {
    // A pasted invite link (or a code with spaces/dashes) becomes the bare code.
    const parsed = raw.length > ROOM_CODE_LENGTH ? parseRoomInput(raw) : null;
    setCode(parsed ?? sanitizeCodeTyping(raw));
    if (codeErr) setCodeErr('');
    if (error) setError('');
  };

  const submit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    const form = e.currentTarget;
    const valid = parseRoomInput(code);
    const ce = valid ? '' : `Room codes are ${ROOM_CODE_LENGTH} letters and numbers.`;
    const ne = nameError(name);
    flushSync(() => {
      setCodeErr(ce);
      setNameErr(ne);
    });
    if (ce || ne || !valid) {
      focusFirstInvalid(form);
      return;
    }
    setBusy(true);
    setError('');
    const res = await joinWithName(valid, name);
    setBusy(false);
    if (res.ok) {
      navigate(roomPath(valid));
      return;
    }
    // Stay in the lobby: forget the half-started session.
    useRoomStore.getState().clearRoom();
    setError(res.error.message);
  };

  return (
    <form className="card lobby-card" onSubmit={(e) => void submit(e)} aria-labelledby="join-title" noValidate>
      <h2 id="join-title" className="lobby-card__title">
        Join a game
      </h2>
      <p className="lobby-card__lede">Got a code or an invite link? Paste it here.</p>
      <div className="field">
        <label className="field__label" htmlFor={codeId}>
          Room code
        </label>
        <input
          id={codeId}
          className="input input--code"
          value={code}
          onChange={(e) => onCodeChange(e.target.value)}
          onPaste={(e) => {
            const text = e.clipboardData.getData('text');
            const parsed = parseRoomInput(text);
            if (parsed) {
              e.preventDefault();
              onCodeChange(parsed);
            }
          }}
          placeholder="K7PQZ"
          autoComplete="off"
          autoCapitalize="characters"
          spellCheck={false}
          inputMode="text"
          aria-invalid={codeErr ? true : undefined}
          aria-describedby={codeErr ? `${codeId}-err` : undefined}
        />
        {codeErr && (
          <span id={`${codeId}-err`} className="field__error">
            {codeErr}
          </span>
        )}
      </div>
      <NameField
        value={name}
        onChange={(v) => {
          setName(v);
          if (nameErr) setNameErr('');
        }}
        error={nameErr}
      />
      {error && (
        <p className="field__error" role="alert">
          {error}
        </p>
      )}
      <button type="submit" className="btn btn--primary btn--block lobby-card__cta" aria-disabled={busy || undefined}>
        {busy ? <span className="btn__spinner" aria-hidden="true" /> : <IconArrowRight />}
        Join
      </button>
    </form>
  );
}

function ResumeList() {
  const [rooms, setRooms] = useState<RecentRoom[]>(() => getRecentRooms());
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 60_000);
    return () => window.clearInterval(t);
  }, []);
  if (rooms.length === 0) return null;
  return (
    <section className="resume" aria-labelledby="resume-title">
      <h2 id="resume-title" className="eyebrow">
        Resume
      </h2>
      <ul className="resume__list">
        {rooms.map((r) => (
          <li key={r.code} className="resume__item">
            <a
              className="resume__link"
              href={roomPath(r.code)}
              onClick={(e) => {
                if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                e.preventDefault();
                navigate(roomPath(r.code));
              }}
            >
              <span className="resume__avatar" aria-hidden="true">
                <IconUser />
              </span>
              <span className="resume__text">
                <span className="resume__who">{r.opponent ? `vs ${r.opponent}` : 'Waiting for an opponent'}</span>
                <span className="resume__meta">
                  Room <span className="resume__code">{r.code}</span> · {relativeTime(r.at, now)}
                </span>
              </span>
              <span className="resume__go" aria-hidden="true">
                <IconArrowRight />
              </span>
            </a>
            <button
              type="button"
              className="btn btn--ghost btn--icon btn--sm resume__forget"
              aria-label={`Forget room ${r.code}`}
              title="Forget this room"
              onClick={() => {
                forgetRoom(r.code);
                setRooms(getRecentRooms());
              }}
            >
              <IconClose />
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function Lobby() {
  const [name, setName] = useState(() => storedName());
  // Back from a room: start at the top, not at the old room's scroll offset.
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return (
    <div className="lobby">
      <main className="lobby__main" id="main">
        <header className="lobby__hero">
          <h1 className="sr-only">Wordloom</h1>
          <Wordmark />
          <p className="lobby__pitch">A word game for two. Weave words across the board, together in real time.</p>
        </header>

        <div className="lobby__cards">
          <CreateCard name={name} setName={setName} />
          <JoinCard name={name} setName={setName} />
        </div>

        <ResumeList />

        <details className={clsx('card', 'lobby__howto')}>
          <summary>
            <span>How to play</span>
            <span className="lobby__howto-hint">Rules, scoring and the premium squares</span>
          </summary>
          <HowToPlayContent />
        </details>
      </main>

      <footer className="app-footer lobby__footer">
        <ThemeToggle />
        <SoundToggle />
        <VersionPill />
      </footer>
    </div>
  );
}
